import { ArrowDown2 } from "iconsax-reactjs";
import { AlertTriangle, X } from "lucide-react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";

type DeleteRoleModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  membersNeedingReassignment: number;
  reassignmentRoleName: string;
  canChooseReassignment: boolean;
  onOpenReassignmentRolesModal: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  error?: string | null;
};

const DeleteRoleModal = ({
  open,
  onOpenChange,
  roleName,
  membersNeedingReassignment,
  reassignmentRoleName,
  canChooseReassignment,
  onOpenReassignmentRolesModal,
  onCancel,
  onDelete,
  isDeleting,
  error,
}: DeleteRoleModalProps) => {
  const requiresReassignment = membersNeedingReassignment > 0;
  const canDelete =
    !isDeleting &&
    (!requiresReassignment ||
      (canChooseReassignment && reassignmentRoleName.length > 0));

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader className="relative justify-center">
        <ModalTitle className="text-center">Delete this role?</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="absolute right-3 top-3 rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
          disabled={isDeleting}
        >
          <X size={25} />
        </button>
      </ModalHeader>

      <ModalBody className="space-y-6 pt-4">
        <p className="text-center text-[16px] font-[400] leading-7 text-[#3A3E5B]">
          This will remove all associated permissions and members from{" "}
          <span className="font-[500]">{roleName}</span>.
        </p>

        {requiresReassignment ? (
          <>
            <div className="flex items-center gap-3 rounded-[16px] bg-[#F7F2E3] px-5 py-4 text-[16px] font-[400] text-[#161827]">
              <AlertTriangle size={24} className="shrink-0 text-[#D97706]" />
              <span>
                This role has{" "}
                <span className="font-[500] text-[#B45309]">
                  {membersNeedingReassignment} members
                </span>{" "}
                without another role assigned
              </span>
            </div>

            <div>
              <div className="text-[16px] font-[500] text-[#3A3E5B]">
                Reassign members to
              </div>
              <button
                type="button"
                onClick={onOpenReassignmentRolesModal}
                className="mt-3 flex h-12 w-full items-center justify-between rounded-[14px] bg-[#E9ECFA] px-4 text-left text-[16px] font-[400] text-[#737AA8]"
                disabled={!canChooseReassignment || isDeleting}
              >
                <span className="truncate">
                  {canChooseReassignment
                    ? reassignmentRoleName || "Select role"
                    : "No role available"}
                </span>
                <ArrowDown2 size={18} className="text-[#737AA8]" />
              </button>
            </div>
          </>
        ) : null}

        {error ? <p className="text-[14px] text-[#C24E4E]">{error}</p> : null}
      </ModalBody>

      <ModalFooter className="gap-8">
        <Button
          variant="ghost"
          className="h-11 rounded-[14px] px-8 text-[16px] font-[500] text-[#3A3E5B]"
          onClick={onCancel}
          disabled={isDeleting}
        >
          Go back
        </Button>
        <Button
          className="h-11 rounded-[14px] bg-[#FF3F3F] px-8 text-[16px] font-[500] text-white hover:bg-[#E23737]"
          onClick={onDelete}
          disabled={!canDelete}
        >
          {isDeleting
            ? "Deleting..."
            : requiresReassignment
              ? "Delete permanently"
              : "Delete role"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteRoleModal;
